<template>
  <div id="material-settings">
    <div id="view-sub-nav">
      <v-btn variant="outlined" color="primary" @click="openCreateDialog">Create New Material</v-btn>
      <v-spacer />
      <v-switch
        v-if="$store.state.user.permissions.admin"
        v-model="showArchived"
        label="Show archived"
        hide-details
        density="compact"
        class="mr-4"
        @update:model-value="onShowArchivedChange"
      ></v-switch>
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        density="compact"
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      v-if="materials"
      :items="tableItems"
      :loading="loading || loadingArchived"
      :headers="tableHeaders"
      :sort-by="showArchived ? [{ key: 'dateArchived', order: 'desc' }] : [{ key: 'text', order: 'asc' }]"
      :group-by="showArchived ? [{ key: 'value', order: 'asc' }] : []"
      :items-per-page="25"
      :search="tableSearch"
      id="materials-table"
    >
      <template v-slot:group-header="{ item, columns, isGroupOpen, toggleGroup }">
        <tr>
          <td :colspan="columns.length" class="bg-grey-lighten-4" style="cursor: pointer" @click="toggleGroup(item)">
            <v-icon size="small" class="mr-2">{{
              isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right'
            }}</v-icon>
            <strong>{{ (item.items[0] && item.items[0].raw.text) || item.value }}</strong>
            <span class="text-caption text-grey-darken-1 ml-2">{{ item.value }}</span>
            <span class="text-caption text-grey ml-2"
              >· {{ item.items.length }} archived version{{ item.items.length === 1 ? '' : 's' }}</span
            >
            <v-chip v-if="hasLiveCounterpart(item.value)" size="x-small" color="green-lighten-4" class="ml-3"
              >has live</v-chip
            >
            <v-chip v-else size="x-small" color="orange-lighten-4" class="ml-3">discontinued</v-chip>
          </td>
        </tr>
      </template>
      <template v-slot:item.regions="{ item }">
        {{ regionLabels(item.regions) }}
      </template>
      <template v-slot:item.hasGrain="{ item }">
        {{ item.hasGrain ? 'True' : 'False' }}
      </template>
      <template v-slot:item.colours="{ item }">
        {{ item.colours && item.colours.length ? item.colours.length : '-' }}
      </template>
      <template v-slot:item.sizes="{ item }">
        {{ item.sizes.length }}
      </template>
      <template v-slot:item.dateArchived="{ item }">
        {{ item.dateArchived ? $moment(item.dateArchived.toDate()).format('Do MMM YYYY') : '—' }}
      </template>
      <template v-slot:item.actions="{ item }">
        <td class="d-flex justify-end align-center">
          <v-tooltip v-if="$store.state.user.permissions.admin && !item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon size="small" class="mr-2" @click="openReplaceDialog(item)" v-bind="props">mdi-pencil</v-icon>
            </template>
            <span>Edit Material (archives old, creates new live)</span>
          </v-tooltip>
          <v-tooltip v-if="$store.state.user.permissions.admin && !item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon size="small" @click="openArchiveOnlyDialog(item)" v-bind="props">mdi-archive-arrow-down</v-icon>
            </template>
            <span>Archive Material (discontinue, no replacement)</span>
          </v-tooltip>
          <v-tooltip v-if="item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon
                size="small"
                class="mr-2"
                @click="
                  () => {
                    selectedMaterial = item
                    sizesDialog = true
                  }
                "
                v-bind="props"
                >mdi-image-size-select-small</v-icon
              >
            </template>
            <span>View Sheets</span>
          </v-tooltip>
          <v-tooltip v-if="item.archived" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon
                :disabled="item.hasGrain"
                size="small"
                class="mr-2"
                @click="
                  () => {
                    selectedMaterial = item
                    coloursDialog = true
                  }
                "
                v-bind="props"
                >mdi-palette</v-icon
              >
            </template>
            <span>View Colours</span>
          </v-tooltip>
          <v-tooltip
            v-if="$store.state.user.permissions.admin && item.archived && !hasLiveCounterpart(item.value)"
            location="top"
            :open-delay="500"
          >
            <template v-slot:activator="{ props }">
              <v-icon size="small" @click="openReviveDialog(item)" v-bind="props">mdi-archive-arrow-up</v-icon>
            </template>
            <span>Revive Material (creates new live)</span>
          </v-tooltip>
        </td>
      </template>
    </v-data-table>

    <!-- Sizes -->
    <v-dialog
      v-model="sizesDialog"
      v-if="selectedMaterial"
      fullscreen
      transition="slide-x-reverse-transition"
      origin="align-center"
      :max-width="300"
      attach="#materials-table"
      @update:model-value="onSizesClose"
    >
      <v-card class="mb-3 elevation-0">
        <v-card-title class="text-subtitle-1">
          <div class="text-subtitle-1">
            {{ selectedMaterial.text }} Sheet Sizes
            <v-chip v-if="isSelectedArchived" size="x-small" color="grey-lighten-2" class="ml-2">Archived</v-chip>
          </div>
          <v-spacer></v-spacer>
          <v-icon
            v-if="!isSelectedArchived"
            @click="
              () => {
                selectedMaterial.sizes.push({
                  w: null,
                  h: null,
                  thickness: null,
                  cost: null
                })
              }
            "
            >mdi-plus</v-icon
          >
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row v-for="(sheet, key) in selectedMaterial.sizes" :key="key">
            <v-col cols="2" v-if="!selectedMaterial.hasGrain">
              <v-select
                hide-details
                :readonly="isSelectedArchived"
                v-model.number="sheet.sides"
                :items="[
                  { text: 'Single', value: 1 },
                  { text: 'Double', value: 2 }
                ]"
                label="Sides"
              ></v-select>
            </v-col>
            <v-col cols="3">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model.number="sheet.w"
                label="Width (long length)"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model.number="sheet.h"
                label="Height"
              ></v-text-field>
            </v-col>
            <v-col :cols="selectedMaterial.hasGrain ? 3 : 2">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model.number="sheet.thickness"
                label="Thickness"
              ></v-text-field>
            </v-col>
            <v-col :cols="selectedMaterial.hasGrain ? 3 : 2" class="d-flex align-baseline">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model.number="sheet.cost"
                label="Cost"
                prefix="£"
              ></v-text-field>
              <v-icon v-if="!isSelectedArchived" class="ml-2" @click="deleteSize(selectedMaterial, key)"
                >mdi-delete</v-icon
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- COLOURS -->
    <v-dialog
      v-model="coloursDialog"
      v-if="selectedMaterial"
      fullscreen
      transition="slide-x-reverse-transition"
      origin="align-center"
      :max-width="300"
      attach="#materials-table"
      @update:model-value="onColoursClose"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          <div class="text-subtitle-1">
            {{ selectedMaterial.text }} Colours
            <v-chip v-if="isSelectedArchived" size="x-small" color="grey-lighten-2" class="ml-2">Archived</v-chip>
          </div>
          <v-spacer></v-spacer>
          <v-icon
            v-if="!isSelectedArchived"
            @click="
              () => {
                selectedMaterial.colours.push({
                  value: null,
                  text: null,
                  hex: '#FFFFFF'
                })
              }
            "
            >mdi-plus</v-icon
          >
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row v-for="(colour, key) in selectedMaterial.colours" :key="key">
            <v-col cols="5">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model="colour.value"
                label="Value"
              ></v-text-field>
            </v-col>
            <v-col cols="5">
              <v-text-field
                hide-details
                :readonly="isSelectedArchived"
                v-model="colour.text"
                label="Name"
              ></v-text-field>
            </v-col>
            <v-spacer />
            <v-col cols="1" class="d-flex align-center justify-end">
              <v-menu :close-on-content-click="false" :disabled="isSelectedArchived">
                <template v-slot:activator="{ props }">
                  <div class="colour" :style="{ 'background-color': colour.hex }" v-bind="props"></div>
                </template>
                <v-color-picker v-model="colour.hex" label="Colour" color-pick mode="hexa"></v-color-picker>
              </v-menu>
              <v-icon v-if="!isSelectedArchived" @click="deleteColour(selectedMaterial, key)">mdi-delete</v-icon>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- CREATE / REPLACE (archive old + create new) / REVIVE -->
    <v-dialog v-model="replaceDialog" v-if="replaceDraft" width="900" scrollable>
      <v-card>
        <v-card-title class="bg-primary text-white text-h6 pa-0">
          <div class="px-4 pt-3 pb-1">{{ replaceDialogTitle }}</div>
          <v-tabs v-model="replaceTab" bg-color="primary" slider-color="accent" class="px-2">
            <v-tab>
              Material
              <v-icon v-if="replaceTabErrors.details" size="small" color="error" class="ml-1">mdi-alert-circle</v-icon>
            </v-tab>
            <v-tab>
              Sheets
              <v-icon v-if="replaceTabErrors.sizes" size="small" color="error" class="ml-1">mdi-alert-circle</v-icon>
            </v-tab>
            <v-tab :disabled="replaceDraft.hasGrain">
              Colours
              <v-icon v-if="replaceTabErrors.colours" size="small" color="error" class="ml-1">mdi-alert-circle</v-icon>
            </v-tab>
          </v-tabs>
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 80vh">
          <div v-if="replaceMode === 'create'" class="text-caption mb-3">
            Confirming will create a new live material with these values.
          </div>
          <div v-else-if="replaceMode === 'revive'" class="text-caption mb-3">
            Confirming will create a new live <strong>{{ selectedMaterial && selectedMaterial.text }}</strong> material
            with these values. The archived version stays archived as part of the audit trail.
          </div>
          <div v-else class="text-caption mb-3">
            Confirming will archive the current <strong>{{ selectedMaterial && selectedMaterial.text }}</strong> and
            create a new live version with these values. Existing quotes automatically re-price to the new version;
            existing orders stay frozen to what was sold.
          </div>
          <v-form ref="replaceForm">
            <v-tabs-window v-model="replaceTab">
              <!-- Details -->
              <v-tabs-window-item eager>
                <v-text-field
                  label="Code"
                  v-model="replaceDraft.value"
                  :disabled="replaceMode !== 'create'"
                  :rules="[(v) => !!v || 'Code is required — ask Tim if you do not know what this should be!']"
                ></v-text-field>
                <v-text-field
                  label="Name"
                  v-model="replaceDraft.text"
                  :rules="[(v) => !!v || 'Name is required']"
                ></v-text-field>
                <v-select
                  v-model="replaceDraft.regions"
                  :items="regions"
                  label="Regions"
                  :rules="[(v) => !!v || 'Regions required']"
                  multiple
                  chips
                ></v-select>
                <v-checkbox
                  v-model="replaceDraft.hasGrain"
                  label="Has Grain"
                  @update:model-value="onHasGrainChange"
                ></v-checkbox>
                <template v-if="replaceDraft.hasGrain">
                  <v-color-picker
                    v-model="replaceDraft.texture.hex"
                    label="Colour"
                    color-pick
                    mode="hexa"
                  ></v-color-picker>
                  <v-text-field
                    label="Image"
                    v-model="replaceDraft.texture.image"
                    :rules="[(v) => !!v || 'Image is required']"
                  ></v-text-field>
                </template>
              </v-tabs-window-item>

              <!-- Sheet Sizes -->
              <v-tabs-window-item eager>
                <div class="d-flex align-center mb-2">
                  <div class="text-subtitle-1">Sheet Sizes</div>
                  <v-spacer></v-spacer>
                  <v-icon @click="replaceDraft.sizes.push({ w: null, h: null, thickness: null, cost: null, sides: 1 })"
                    >mdi-plus</v-icon
                  >
                </div>
                <div v-if="!replaceDraft.sizes.length" class="text-caption text-grey py-4 text-center">
                  No sheet sizes — click + to add one.
                </div>
                <v-row v-for="(sheet, key) in replaceDraft.sizes" :key="'s' + key">
                  <v-col cols="2" v-if="!replaceDraft.hasGrain">
                    <v-select
                      hide-details
                      v-model.number="sheet.sides"
                      :items="[
                        { text: 'Single', value: 1 },
                        { text: 'Double', value: 2 }
                      ]"
                      label="Sides"
                    ></v-select>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field hide-details v-model.number="sheet.w" label="Width (long length)"></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field hide-details v-model.number="sheet.h" label="Height"></v-text-field>
                  </v-col>
                  <v-col :cols="replaceDraft.hasGrain ? 3 : 2">
                    <v-text-field hide-details v-model.number="sheet.thickness" label="Thickness"></v-text-field>
                  </v-col>
                  <v-col :cols="replaceDraft.hasGrain ? 3 : 2" class="d-flex align-baseline">
                    <v-text-field hide-details v-model.number="sheet.cost" label="Cost" prefix="£"></v-text-field>
                    <v-icon class="ml-2" @click="replaceDraft.sizes.splice(key, 1)">mdi-delete</v-icon>
                  </v-col>
                </v-row>
              </v-tabs-window-item>

              <!-- Colours -->
              <v-tabs-window-item eager>
                <div v-if="replaceDraft.hasGrain" class="text-caption text-grey py-4 text-center">
                  Grain materials don't use a colour list.
                </div>
                <template v-else>
                  <div class="d-flex align-center mb-2">
                    <div class="text-subtitle-1">Colours</div>
                    <v-spacer></v-spacer>
                    <v-icon @click="replaceDraft.colours.push({ value: null, text: null, hex: '#FFFFFF' })"
                      >mdi-plus</v-icon
                    >
                  </div>
                  <div v-if="!replaceDraft.colours.length" class="text-caption text-grey py-4 text-center">
                    No colours — click + to add one.
                  </div>
                  <v-row v-for="(colour, key) in replaceDraft.colours" :key="'c' + key">
                    <v-col cols="5">
                      <v-text-field hide-details v-model="colour.value" label="Value"></v-text-field>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field hide-details v-model="colour.text" label="Name"></v-text-field>
                    </v-col>
                    <v-spacer />
                    <v-col cols="1" class="d-flex align-center justify-end">
                      <v-menu :close-on-content-click="false">
                        <template v-slot:activator="{ props }">
                          <div class="colour" :style="{ 'background-color': colour.hex }" v-bind="props"></div>
                        </template>
                        <v-color-picker v-model="colour.hex" label="Colour" color-pick mode="hexa"></v-color-picker>
                      </v-menu>
                      <v-icon @click="replaceDraft.colours.splice(key, 1)">mdi-delete</v-icon>
                    </v-col>
                  </v-row>
                </template>
              </v-tabs-window-item>
            </v-tabs-window>
          </v-form>
          <v-text-field
            v-if="replaceError"
            v-model="replaceError"
            error
            variant="outlined"
            readonly
            class="mt-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="closeReplaceDialog" :disabled="submitting">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="confirmReplacement" :disabled="submitting || !replaceDirty">{{
            replaceConfirmLabel
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ARCHIVE ONLY (discontinue, no replacement) -->
    <v-dialog v-model="archiveOnlyDialog" max-width="480" persistent>
      <v-card>
        <div class="px-4 pt-3 pb-1">Archive Material</div>
        <v-card-text>
          <div class="text-caption mb-3">
            Confirming will discontinue <strong>{{ archiveTarget && archiveTarget.text }}</strong> with no replacement.
            New quotes won't list it; existing orders stay frozen to what was sold, and quotes referencing it fall back
            to this archived version.
          </div>
          <v-alert v-if="linkedWorktops.length" density="compact" text type="warning" class="text-caption mb-3">
            This will also discontinue the colour scheme on {{ linkedWorktops.length }} worktop{{
              linkedWorktops.length === 1 ? '' : 's'
            }}: <strong>{{ linkedWorktops.map((w) => w.text).join(', ') }}</strong
            >. Each will be archived and republished without a colour scheme. Existing quotes and orders keep the colour
            they were saved with.
          </v-alert>
          <v-text-field
            v-if="archiveOnlyError"
            v-model="archiveOnlyError"
            error
            variant="outlined"
            readonly
            class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="archiveOnlyDialog = false" :disabled="submitting">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="confirmArchiveOnly" :disabled="submitting">Archive</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { db } from '@/js/db'
import { collection, doc, getDocs, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore'

export default {
  name: 'MaterialSettings',
  data() {
    return {
      loading: true,
      tableSearch: null,
      headers: [
        {
          title: 'Name',
          sortable: false,
          key: 'text'
        },
        {
          title: 'Regions',
          sortable: false,
          key: 'regions'
        },
        {
          title: 'Wood Veneer',
          sortable: false,
          key: 'hasGrain'
        },
        {
          title: 'Colours',
          sortable: false,
          key: 'colours'
        },
        {
          title: 'Sheet Sizes',
          sortable: false,
          key: 'sizes'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          key: 'actions'
        }
      ],
      sizesDialog: false,
      coloursDialog: false,
      //
      replaceDialog: false,
      replaceDraft: null,
      replaceMode: 'replace',
      replaceTab: 0,
      replaceError: null,
      replaceOriginalJSON: null,
      archiveOnlyDialog: false,
      archiveTarget: null,
      archiveOnlyError: null,
      showArchived: false,
      archivedMaterialsList: [],
      loadingArchived: false,
      submitting: false,
      selectedMaterial: null,
      regions: [
        { text: 'UK', value: 'uk' },
        { text: 'EU', value: 'eu' },
        { text: 'US', value: 'us' }
      ],
      materials: null
    }
  },
  computed: {
    liveMaterials() {
      return (this.materials || []).filter((m) => !m.archived)
    },
    tableItems() {
      return this.showArchived ? this.archivedMaterialsList : this.liveMaterials
    },
    tableHeaders() {
      if (!this.showArchived) return this.headers
      return [
        { key: 'data-table-group', title: '', width: 0 },
        { title: 'Name', sortable: false, key: 'text' },
        { title: 'Regions', sortable: false, key: 'regions' },
        { title: 'Wood Veneer', sortable: false, key: 'hasGrain' },
        { title: 'Colours', sortable: false, key: 'colours' },
        { title: 'Sheet Sizes', sortable: false, key: 'sizes' },
        { title: 'Archived', sortable: true, key: 'dateArchived' },
        { title: 'Actions', sortable: false, align: 'end', key: 'actions' }
      ]
    },
    isSelectedArchived() {
      return !!(this.selectedMaterial && this.selectedMaterial.archived)
    },
    replaceDialogTitle() {
      if (this.replaceMode === 'create') return 'Create New Material'
      if (this.replaceMode === 'revive') return 'Revive Material'
      return 'Edit Material'
    },
    replaceConfirmLabel() {
      if (this.replaceMode === 'create') return 'Create'
      if (this.replaceMode === 'revive') return 'Confirm Revival'
      return 'Confirm Replacement'
    },
    replaceTabErrors() {
      const d = this.replaceDraft
      if (!d) return { details: false, sizes: false, colours: false }
      const details = !d.value || !d.text || !d.regions || !d.regions.length || (d.hasGrain && !d.texture.image)
      const colours = !d.hasGrain && d.colours.some((c) => !c.value || !c.text)
      return { details, sizes: !!this.sizesError(d), colours }
    },
    replaceDirty() {
      if (this.replaceMode !== 'replace') return true
      if (!this.replaceDraft) return false
      return JSON.stringify(this.replaceDraft) !== this.replaceOriginalJSON
    },
    linkedWorktops() {
      if (!this.archiveTarget) return []
      return (this.$store.state.worktops || []).filter((w) => w.material === this.archiveTarget.value)
    }
  },
  async mounted() {
    await this.$live('materials', collection(db, 'materials'))
    this.loading = false
  },
  methods: {
    regionLabels(regions) {
      if (!regions || !regions.length) return '—'
      return regions
        .map((code) => this.regions.find((r) => r.value === code)?.text || String(code).toUpperCase())
        .join(', ')
    },
    // Sheet rows must be complete, and non-grain (coloured) sheets must carry a `sides`
    // value: Materials.getSheetCostFromProperties matches those sheets on `sides` with
    // strict equality, so a row saved without one can never be priced. Grain materials
    // are matched on w/h/thickness only, so sides is irrelevant there.
    // Returns an error string, or null when valid.
    sizesError(draft) {
      if (!draft.sizes.length) return 'Add at least one sheet size.'
      const incomplete = draft.sizes.some((s) => !s.w || !s.h || !s.thickness || s.cost == null)
      if (incomplete) return 'Every sheet needs a width, height, thickness and cost.'
      if (!draft.hasGrain && draft.sizes.some((s) => s.sides !== 1 && s.sides !== 2)) {
        return 'Every sheet needs Sides set to Single or Double.'
      }
      return null
    },
    normaliseSizes(draft) {
      return draft.sizes.map((s) => {
        const sheet = { w: s.w, h: s.h, thickness: s.thickness, cost: s.cost }
        if (!draft.hasGrain) sheet.sides = s.sides
        return sheet
      })
    },
    defaultTexture() {
      return { hex: '#FFFFFF', image: null, repeat: { u: 1, v: 1 } }
    },
    onHasGrainChange(hasGrain) {
      if (hasGrain) {
        this.replaceDraft.colours = []
        this.replaceDraft.sizes.forEach((sheet) => delete sheet.sides)
      } else {
        this.replaceDraft.texture = this.defaultTexture()
        this.replaceDraft.sizes.forEach((sheet) => (sheet.sides = 1))
      }
    },
    async onShowArchivedChange(value) {
      if (!value) return
      if (this.archivedMaterialsList.length) return
      this.loadingArchived = true
      try {
        const snap = await getDocs(query(collection(db, 'materials'), where('archived', '==', true)))
        this.archivedMaterialsList = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      } catch (err) {
        this.$store.commit('openSnackbar', { snackbarText: 'Error loading archived: ' + err.message })
      } finally {
        this.loadingArchived = false
      }
    },
    // Also builds the blank draft for create mode, when passed {}.
    buildReplaceDraft(material) {
      return {
        value: material.value || null,
        text: material.text || null,
        regions: material.regions ? [...material.regions] : [],
        stock: material.stock || null,
        texture: material.texture
          ? {
              hex: material.texture.hex || '#FFFFFF',
              image: material.texture.image || null,
              repeat: material.texture.repeat ? { ...material.texture.repeat } : { u: 1, v: 1 }
            }
          : this.defaultTexture(),
        hasGrain: !!material.hasGrain,
        sizes: (material.sizes || []).map((s) => ({ ...s })),
        colours: (material.colours || []).filter((c) => c.value !== 'tbc').map((c) => ({ ...c }))
      }
    },
    openCreateDialog() {
      this.selectedMaterial = null
      this.replaceMode = 'create'
      this.replaceDraft = this.buildReplaceDraft({})
      this.replaceOriginalJSON = JSON.stringify(this.replaceDraft)
      this.replaceError = null
      this.replaceTab = 0
      this.replaceDialog = true
    },
    openReplaceDialog(material) {
      this.selectedMaterial = material
      this.replaceMode = 'replace'
      this.replaceDraft = this.buildReplaceDraft(material)
      this.replaceOriginalJSON = JSON.stringify(this.replaceDraft)
      this.replaceError = null
      this.replaceTab = 0
      this.replaceDialog = true
    },
    openReviveDialog(material) {
      this.selectedMaterial = material
      this.replaceMode = 'revive'
      this.replaceDraft = this.buildReplaceDraft(material)
      this.replaceOriginalJSON = JSON.stringify(this.replaceDraft)
      this.replaceError = null
      this.replaceTab = 0
      this.replaceDialog = true
    },
    hasLiveCounterpart(value) {
      return this.liveMaterials.some((m) => m.value === value)
    },
    closeReplaceDialog() {
      this.replaceDialog = false
      this.replaceDraft = null
      this.replaceOriginalJSON = null
      this.replaceError = null
      this.replaceMode = 'replace'
    },
    async confirmReplacement() {
      if (!this.replaceDirty) {
        this.replaceError = 'No changes to save.'
        return
      }
      const formValid = await this.$utils.validateForm(this.$refs.replaceForm)
      const errs = this.replaceTabErrors
      if (!formValid || errs.details || errs.sizes || errs.colours) {
        if (errs.details) this.replaceTab = 0
        else if (errs.sizes) this.replaceTab = 1
        else if (errs.colours) this.replaceTab = 2
        this.replaceError =
          errs.sizes && !errs.details && !errs.colours
            ? this.sizesError(this.replaceDraft)
            : 'Please fix the highlighted fields before confirming.'
        return
      }
      this.replaceError = null
      this.submitting = true
      try {
        const value = this.replaceDraft.value
        const isCreate = this.replaceMode === 'create'
        const isRevive = this.replaceMode === 'revive'

        if (isRevive && this.hasLiveCounterpart(value)) {
          this.replaceError = `A live material with code '${value}' already exists. Use Archive & Replace on it instead.`
          this.submitting = false
          return
        }

        if (isCreate) {
          const existing = await getDocs(query(collection(db, 'materials'), where('value', '==', value)))
          if (!existing.empty) {
            const hasLive = existing.docs.some((d) => !d.data().archived)
            this.replaceError = hasLive
              ? `Code '${value}' is already in use by a live material. Use Edit on the existing row to update it.`
              : `Code '${value}' is in use by an archived material. Revive it from the Archived list instead.`
            this.replaceTab = 0
            this.submitting = false
            return
          }
        }

        const hasGrain = this.replaceDraft.hasGrain
        const replacement = {
          value: this.replaceDraft.value,
          text: this.replaceDraft.text,
          regions: this.replaceDraft.regions,
          stock: this.replaceDraft.stock,
          texture: hasGrain ? this.replaceDraft.texture : this.defaultTexture(),
          hasGrain: hasGrain,
          sizes: this.normaliseSizes(this.replaceDraft),
          colours: hasGrain ? [] : this.replaceDraft.colours
        }
        const newRef = doc(collection(db, 'materials'))

        if (isCreate) {
          // Nothing to archive — this is the first version of this code.
          await setDoc(newRef, replacement)
          this.$store.commit('openSnackbar', { snackbarText: 'Material created' })
        } else if (isRevive) {
          await setDoc(newRef, replacement)
          this.$store.commit('openSnackbar', {
            snackbarText: 'Material revived — new live version created'
          })
        } else {
          const priorSnap = await getDocs(
            query(collection(db, 'materials'), where('value', '==', value), where('archived', '==', true))
          )
          let prevArchivedDate = null
          priorSnap.forEach((docSnap) => {
            const d = docSnap.data().dateArchived
            if (d && (!prevArchivedDate || d.seconds > prevArchivedDate.seconds)) prevArchivedDate = d
          })

          const now = this.$utils.tsNow()
          const batch = writeBatch(db)

          const oldRef = doc(db, 'materials', this.selectedMaterial.id)
          batch.update(oldRef, {
            archived: true,
            dateArchived: now,
            prevArchivedDate: prevArchivedDate
          })
          batch.set(newRef, replacement)

          await batch.commit()

          this.$store.commit('openSnackbar', {
            snackbarText: 'Material replaced — old version archived'
          })
        }

        await this.refreshArchivedList()
        this.closeReplaceDialog()
      } catch (err) {
        this.replaceError = 'Error: ' + (err.message || err)
      } finally {
        this.submitting = false
      }
    },
    async refreshArchivedList() {
      this.archivedMaterialsList = []
      if (this.showArchived) await this.onShowArchivedChange(true)
    },
    openArchiveOnlyDialog(material) {
      this.archiveTarget = material
      this.archiveOnlyError = null
      this.archiveOnlyDialog = true
    },
    async prevArchivedDateFor(collectionName, value) {
      const priorSnap = await getDocs(
        query(collection(db, collectionName), where('value', '==', value), where('archived', '==', true))
      )
      let prev = null
      priorSnap.forEach((docSnap) => {
        const d = docSnap.data().dateArchived
        if (d && (!prev || d.seconds > prev.seconds)) prev = d
      })
      return prev
    },
    async confirmArchiveOnly() {
      if (!this.archiveTarget) return
      this.archiveOnlyError = null
      this.submitting = true
      try {
        const value = this.archiveTarget.value
        const now = this.$utils.tsNow()

        const matPrev = await this.prevArchivedDateFor('materials', value)
        const linked = this.linkedWorktops
        const worktopPlans = []
        for (const wt of linked) {
          worktopPlans.push({ wt, prev: await this.prevArchivedDateFor('worktops', wt.value) })
        }

        const batch = writeBatch(db)
        // Archive the material itself.
        batch.update(doc(db, 'materials', this.archiveTarget.id), {
          archived: true,
          dateArchived: now,
          prevArchivedDate: matPrev
        })
        // Cascade: archive each linked live worktop and republish it without a
        // colour scheme (material link removed). See WORKTOP_ARCHIVING.md.
        for (const { wt, prev } of worktopPlans) {
          batch.update(doc(db, 'worktops', wt.id), {
            archived: true,
            dateArchived: now,
            prevArchivedDate: prev
          })
          batch.set(doc(collection(db, 'worktops')), {
            value: wt.value,
            text: wt.text,
            regions: wt.regions || (wt.region ? [wt.region] : []),
            material: null,
            sizes: (wt.sizes || []).map((s) => ({ ...s }))
          })
        }

        await batch.commit()

        this.$store.commit('openSnackbar', {
          snackbarText: worktopPlans.length
            ? `Material archived — ${worktopPlans.length} linked worktop(s) republished without a colour scheme`
            : 'Material archived — discontinued with no replacement'
        })
        await this.refreshArchivedList()
        this.archiveOnlyDialog = false
        this.archiveTarget = null
      } catch (err) {
        this.archiveOnlyError = 'Error: ' + (err.message || err)
      } finally {
        this.submitting = false
      }
    },
    async deleteColour(material, index) {
      material.colours.splice(index, 1)
      await updateDoc(doc(db, 'materials', material.id), {
        colours: material.colours
      })
      this.$store.commit('openSnackbar', { snackbarText: 'Colour Deleted' })
    },
    async deleteSize(material, index) {
      material.sizes.splice(index, 1)
      await updateDoc(doc(db, 'materials', material.id), {
        sizes: material.sizes
      })
      this.$store.commit('openSnackbar', { snackbarText: 'Sheet Size Deleted' })
    },
    // TODO only save when updated - need to track changed param locally with emitters from dialog fields
    async onColoursClose() {
      if (!this.selectedMaterial || this.selectedMaterial.archived) return
      await updateDoc(doc(db, 'materials', this.selectedMaterial.id), {
        colours: this.selectedMaterial.colours
      })
      this.$store.commit('openSnackbar', { snackbarText: 'Colours Updated' })
    },
    async onSizesClose() {
      if (!this.selectedMaterial || this.selectedMaterial.archived) return
      await updateDoc(doc(db, 'materials', this.selectedMaterial.id), {
        sizes: this.selectedMaterial.sizes
      })
      this.$store.commit('openSnackbar', { snackbarText: 'Sizes Updated' })
    }
  }
}
</script>

<style lang="scss" scoped>
.colour {
  min-width: 30px;
  width: 30px;
  height: 30px;
  border: solid 1px #999;
  border-radius: 50%;
  margin-right: 5px;
}

#material-settings {
  height: 100%;
  display: flex;
  flex-direction: column;
  #materials-table {
    flex: 1;
    position: relative;
  }
}
</style>
